import {faBroom} from "@fortawesome/pro-solid-svg-icons/faBroom";
import {GetItemCounts, useInfiniteMailBox} from "@twigsby/client-data";
import {BatchMailItemType} from "@twigsby/client-data/lib/Types/ForwardingBatchTypes";
import {
    Button,
    ButtonSize,
    ColourSolid,
    ContentBlock,
    ContentLoader, InfiniteLoadTrigger,
    ItemRowHeader,
    Modal,
    ModalSize,
    ModalType,
    ToggleSwitch,
    ToggleType,
    useComboBoxSimple,
    useLayout
} from "@twigsby/frontend-framework";
import {GetArrayIndexByKeyValue} from "@twigsby/helpers";
import React, {useEffect, useState} from "react";
import MailItemMinimal from "../../Mail/MailItemMinimal";
import SelectEverythingBanner from "../../SelectEverythingBanner";

interface SearchMailItemsForBatchModalType extends ModalType {
    batchMailUuids: Array<string>;
}

const SearchMailItemsForBatchModal = (props: SearchMailItemsForBatchModalType) => {
    const { isSmall, isLarge, isXL } = useLayout();

    const {
        allSelected,
        selectedItems,
        visibleIndeterminate,
        visibleSelected,
        ToggleAll,
        ToggleVisible,
        ToggleSelected,
        ResetSelection
    } = useComboBoxSimple();

    const {
        status,
        data,
        error,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        dataUpdatedAt
    } = useInfiniteMailBox({ folderId: 1, includeFolderAssigned: { value: true } } );

    const [excludedItems, setExcludedItems] = useState<Array<string>>(props.batchMailUuids);
    const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 } );

    const AddSelectedToBatch = () => {
        let matchingItems: Array<BatchMailItemType> = [];

        selectedItems.forEach((itemId) => {
            const mailData: Array<any> = data?.pages?.map((page) =>
                page.data?.map((mailItem) => mailItem)
            )[0] || [];

            const mailIndex = GetArrayIndexByKeyValue(mailData, 'mailUuid', itemId);

            let mailItem: BatchMailItemType = {
                mailUuid: mailData[mailIndex].mailUuid,
                files: mailData[mailIndex].files,
                trackingId: mailData[mailIndex].trackingId,
                mailType: mailData[mailIndex].mailType,
                descriptions: [{
                    id: 0,
                    descriptionType: null,
                    description: '',
                    quantity: 0,
                    value: 0,
                    commodityCode: '',
                    originCountryCode: '',
                }]
            }

            matchingItems.push(mailItem);
        });

        props.actionedCallback(matchingItems);
        ResetSelection();
    }

    useEffect(() => {
        if(data) {
            setItemCounts(GetItemCounts(data as any));
        }
    }, [dataUpdatedAt, data]);

    useEffect(() => {
        setExcludedItems(props.batchMailUuids);
    }, [props]);

    return (
        <Modal
            title={'Add Mail Items'}
            isOpen={props.isOpen}
            closeCallback={props.closeCallback}
            modalSize={ModalSize.Large}
            footerContent={
                <Button disabled={selectedItems.length === 0} title={'Add to Batch'} fullWidth={true} colour={ColourSolid.Success} onClick={() => AddSelectedToBatch()} />
            }
        >
            <div className={'mail-item-batch-filter-container'}>
                <ItemRowHeader
                    extraClass={selectedItems.length > 0 ? 'item-row-actions-active' : ''}
                    sticky={true}
                >
                    <div className='item-row-checkbox'>
                        <ToggleSwitch
                            name={`mailItem-all`}
                            label={`mailItem-all`}
                            invalid={false}
                            type={ToggleType.Checkbox}
                            defaultChecked={allSelected || !visibleIndeterminate && visibleSelected}
                            isIndeterminate={!allSelected && visibleIndeterminate}
                            onChange={(e) => ToggleVisible(data, 'mailUuid', !visibleSelected)}
                            hideLabel={true}
                        />
                    </div>

                    {selectedItems.length === 0 && (
                        <>
                            <div>Envelope</div>
                            <div>Content</div>
                            <div>Reference</div>
                            {isLarge && (
                                <div>Tags</div>
                            )}
                            {isXL && (
                                <div>Notes</div>
                            )}
                        </>
                    )}

                    {selectedItems.length > 0 && (
                        <div>
                            <Button
                                title={`Clear Selection (${selectedItems.length})`}
                                icon={faBroom}
                                size={ButtonSize.Small}
                                onClick={() => ResetSelection()}
                            />
                        </div>
                    )}

                    {itemCounts.loaded !== itemCounts.total && selectedItems.length !== itemCounts.total && visibleSelected && (
                        <SelectEverythingBanner
                            selectedCount={allSelected ? itemCounts.total : selectedItems.length}
                            selectedLabel={allSelected ? 'Select loaded only' : `Select ${itemCounts.total} available items`}
                            callback={() => ToggleAll(!allSelected)}
                        />
                    )}
                </ItemRowHeader>

                {status === "loading" ? (
                    <ContentLoader childHeight={'7em'} />
                ) : status === "error" ? (
                    <ContentBlock error={true} title={error.name} content={error.message} />
                ) : (
                    <>
                        {data.pages.map((group, groupIndex) =>
                            group.data.map((mailItem, index) =>
                                <MailItemMinimal
                                    key={`mailItem${groupIndex}${index}${mailItem.mailUuid}`}
                                    index={`${groupIndex}_${index}`}
                                    selected={allSelected || selectedItems.includes(mailItem.mailUuid)}
                                    selectable={true}
                                    callBack={(mailUuid: any, checked: boolean) => ToggleSelected(mailUuid, itemCounts.total) }
                                    disabled={excludedItems.includes(mailItem.mailUuid)}
                                    {...mailItem}
                                />
                            )
                        )}

                        {itemCounts.total > 0 && (
                            <InfiniteLoadTrigger
                                loaded={itemCounts.loaded}
                                totalCount={itemCounts.total}
                                hasNextPage={hasNextPage}
                                fetchNextPage={fetchNextPage}
                                isFetchingNextPage={isFetchingNextPage}
                            />
                        )}
                    </>
                )}
            </div>
        </Modal>
    )
}

export { SearchMailItemsForBatchModal }
